import React, {useMemo} from 'react'
import {BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, Cell} from 'recharts'
import {Text} from '@/components/ui/text/Text'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {
    StyledMainContainer,
    StyledChartContainer,
    StyledTooltipContainer,
    StyledTooltipItem,
    StyledTooltipDot
} from './style'
import {useTheme} from 'styled-components'
import {DashboardCard} from '../dashboard-card/DashboardCard'
import {capitalize, formatCurrency} from '@/utilities/helpers'
import {Grid} from '@/components/ui/grid-atoms/GridAtoms'
import {useTranslation} from '@/translations/i18n'
import {useGetDashboardAnalytics} from '../../query/useGetDashboardAnalytics'
import {useParsedSearchParams} from '@/hooks/useParsedSearchParams'
import {DashboardEconomicSearchParams} from '../../types'
import {formatLocaleDate} from '@/dayjs'
import {DashboardEconomicPerformanceChartSkeleton} from './DashboardEconomicPerformanceChartSkeleton'
import {periodToDistributionLabel} from '../../utils'

interface TooltipProps {
    active?: boolean
    payload?: Array<{
        color: string
        name: string
        value: number
        dataKey: string
    }>
    label?: string
}

const CustomTooltip: React.FC<TooltipProps> = ({active, payload, label}) => {
    if (active && payload && payload.length) {
        return (
            <StyledTooltipContainer>
                <Text palette="neutral" shade="900" size="textSm" weight={600}>
                    {label}
                </Text>
                {payload.map((entry, index) => (
                    <StyledTooltipItem key={index}>
                        <StyledTooltipDot $color={entry.color} />
                        <Text palette="neutral" shade="700" size="textSm" weight={400}>
                            {entry.name}: €{entry.value.toLocaleString()}
                        </Text>
                    </StyledTooltipItem>
                ))}
            </StyledTooltipContainer>
        )
    }
    return null
}

export const DashboardEconomicPerformanceChart: React.FC = () => {
    const {palette} = useTheme()
    const {t} = useTranslation()
    const {searchParams} = useParsedSearchParams(DashboardEconomicSearchParams)
    const {data, isLoading} = useGetDashboardAnalytics({fields: ['graph'], ...searchParams})

    // Helper function to get opacity based on date range
    const getMonthOpacity = (monthDate: string): number => {
        if (!searchParams.startDate || !searchParams.endDate) {
            return 1 // Default opacity if no date range
        }

        const monthDateObj = new Date(monthDate)
        const startDate = new Date(searchParams.startDate)
        const endDate = new Date(searchParams.endDate)

        // Check if month is within the date range
        return monthDateObj >= startDate && monthDateObj <= endDate ? 1 : 0.2
    }

    const difference = useMemo(() => {
        return (data?.graph?.distribution || 0) - (data?.graph?.budgetSaturation || 0)
    }, [data?.graph?.budgetSaturation, data?.graph?.distribution])

    const budgetSaturationPercentage = useMemo(() => {
        if (data?.graph?.budgetSaturation && data?.graph?.annualBudget) {
            return ((data.graph.budgetSaturation / data.graph.annualBudget) * 100).toFixed(2)
        }
        return 0
    }, [data?.graph?.budgetSaturation, data?.graph?.annualBudget])

    if (isLoading) {
        return <DashboardEconomicPerformanceChartSkeleton />
    }

    return (
        <DashboardCard title={t('dashboard:budget_saturation_title')}>
            <Flexbox direction="column" gap={2} fullWidth>
                <Flexbox gap={2} align="center">
                    <Text palette="neutral" shade="900" size="displaySm" weight={700}>
                        {formatCurrency(data?.graph?.budgetSaturation ?? 0)}
                    </Text>
                    <Text palette="neutral" shade="900" size="textXl" weight={500}>
                        ({budgetSaturationPercentage}%)
                    </Text>
                </Flexbox>
                <Grid gap={6} columns={3}>
                    <Flexbox direction="column">
                        <Text palette="neutral" shade="500" size="textSm" weight={400}>
                            {t('dashboard:annual_budget')}
                        </Text>
                        <Text palette="neutral" shade="800" size="textXl" weight={500}>
                            {formatCurrency(data?.graph?.annualBudget ?? 0)}
                        </Text>
                    </Flexbox>
                    <Flexbox direction="column">
                        <Text palette="neutral" shade="500" size="textSm" weight={400}>
                            {t(periodToDistributionLabel[searchParams.range])}
                        </Text>
                        <Text palette="neutral" shade="800" size="textXl" weight={500}>
                            {formatCurrency(data?.graph?.distribution ?? 0)}
                        </Text>
                    </Flexbox>
                    <Flexbox direction="column">
                        <Text palette="neutral" shade="500" size="textSm" weight={400}>
                            {t('dashboard:usable_difference')}
                        </Text>
                        <Text palette="neutral" shade="800" size="textXl" weight={500}>
                            {formatCurrency(difference)}
                        </Text>
                    </Flexbox>
                </Grid>
                <StyledMainContainer direction="column" gap={4} fullWidth>
                    {/* Chart */}
                    <StyledChartContainer>
                        <ResponsiveContainer width="100%" height={240}>
                            <BarChart
                                data={data?.graph?.year ?? []}
                                barCategoryGap="20%"
                                margin={{left: 60, right: 30}}
                            >
                                <Legend
                                    iconType="circle"
                                    iconSize={8}
                                    verticalAlign="top"
                                    align="right"
                                    height={32}
                                    layout="horizontal"
                                    formatter={value => {
                                        return (
                                            <Text
                                                render="span"
                                                palette="neutral"
                                                shade="600"
                                                size="textSm"
                                                weight={400}
                                            >
                                                {value}
                                            </Text>
                                        )
                                    }}
                                />
                                <CartesianGrid strokeWidth={1} stroke="#f0f0f0" strokeDasharray="3 3" />
                                <XAxis
                                    dataKey="month"
                                    axisLine={false}
                                    tickLine={false}
                                    tick={{fontSize: '13px', fill: palette.neutral[500]}}
                                    tickFormatter={value => capitalize(formatLocaleDate(value, 'MMMM'))}
                                />
                                <YAxis
                                    width={50}
                                    axisLine={false}
                                    tickLine={false}
                                    tick={{fontSize: '13px', fill: palette.neutral[500]}}
                                    tickFormatter={value => formatCurrency(value)}
                                />
                                <Tooltip content={<CustomTooltip />} cursor={{fill: palette.neutral[100]}} />
                                <Bar
                                    dataKey="billed"
                                    radius={[6, 6, 0, 0]}
                                    fill={palette.success[700]}
                                    name={t('dashboard:revenue')}
                                >
                                    {(data?.graph?.year ?? []).map((entry, index) => (
                                        <Cell key={`billed-cell-${index}`} fillOpacity={getMonthOpacity(entry.month)} />
                                    ))}
                                </Bar>
                                <Bar
                                    dataKey="planned"
                                    radius={[6, 6, 0, 0]}
                                    fill={palette.success[300]}
                                    name={t('dashboard:planned')}
                                >
                                    {(data?.graph?.year ?? []).map((entry, index) => (
                                        <Cell
                                            key={`planned-cell-${index}`}
                                            fillOpacity={getMonthOpacity(entry.month)}
                                        />
                                    ))}
                                </Bar>
                                <Bar
                                    dataKey="budget"
                                    radius={[6, 6, 0, 0]}
                                    fill={palette.primary[300]}
                                    name={t('dashboard:budget')}
                                >
                                    {(data?.graph?.year ?? []).map((entry, index) => (
                                        <Cell key={`budget-cell-${index}`} fillOpacity={getMonthOpacity(entry.month)} />
                                    ))}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </StyledChartContainer>
                </StyledMainContainer>
            </Flexbox>
        </DashboardCard>
    )
}
